import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import type { AppHistoryState } from "../app/useTabNavigation";

interface OverlayHistoryState extends AppHistoryState {
  // A stack, not a single marker: two overlays can nest (the questionnaire
  // hub's overlay, and a section pushed on top of it), each with its own
  // `useDismissOnBack` call. A scalar field would have the inner push
  // overwrite the outer's marker, so the outer hook would see its own marker
  // gone the moment the inner one pushed and dismiss itself immediately.
  overlay?: string[];
}

/** Where this hook thinks its history entry is.

    `pushing` is not pedantry: `navigate` is a request, and the location it
    produces arrives on a later render. Without a state distinct from `owned`,
    the render between the two looks exactly like "our entry was popped" —
    which is the condition for dismissing — and the overlay would close itself
    the instant it opened. */
type EntryPhase = "idle" | "pushing" | "owned";

/**
 * Makes the back gesture close an overlay instead of leaving the app.
 *
 * On a phone this is not a nicety. An open sheet held in React state alone is
 * invisible to history, so Android's hardware back — and the iOS standalone
 * edge swipe — skip straight past it and kill the app, discarding whatever the
 * client had typed into it. Every native app treats the thing stacked on top
 * of the screen as what back dismisses first.
 *
 * Implemented through the router rather than `history.pushState` directly:
 * react-router keeps its own index in history state, and going around it
 * leaves that bookkeeping wrong. Going through `navigate` also means this
 * works under MemoryRouter, so the behaviour is testable without a browser.
 *
 * The entry is pushed at the *same* URL, carrying an `overlay` marker in
 * history state. An overlay is a mode of the screen it belongs to, not a
 * place — giving it a URL of its own would make it linkable and reloadable
 * into, which for a half-filled questionnaire is worse than not having it.
 *
 * Only for a controlled overlay: `open` and the state behind `onDismiss` have
 * to be the same piece of state, or the two will fight.
 */
export function useDismissOnBack(open: boolean, onDismiss: () => void, key: string): void {
  const location = useLocation();
  const navigate = useNavigate();
  const phase = useRef<EntryPhase>("idle");
  // Held in a ref so a caller passing a fresh closure every render — the
  // normal thing to write — doesn't re-run the effect and re-push the entry.
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  const onOverlayEntry = ((location.state as OverlayHistoryState | null)?.overlay ?? []).includes(key);

  useEffect(() => {
    // The push landed. Deliberately not an early return: an overlay closed
    // again before its entry arrived still has to be cleaned up below, in the
    // same pass.
    if (phase.current === "pushing" && onOverlayEntry) phase.current = "owned";

    if (open && phase.current === "idle") {
      phase.current = "pushing";
      const currentState = location.state as OverlayHistoryState | null;
      // Spreads the existing state so the tab model's own `fromHome` marker
      // survives the push — losing it here would break the pop back to home
      // for as long as the overlay had been open. Appends to the overlay
      // stack rather than replacing it, so a nested overlay's push doesn't
      // erase the marker an outer `useDismissOnBack` is watching for.
      navigate(location.pathname + location.search, {
        state: {
          ...currentState,
          overlay: [...(currentState?.overlay ?? []), key],
        } satisfies OverlayHistoryState,
      });
      return;
    }

    // Our entry is gone while the overlay is still open: the user pressed
    // back, so honour it. Releasing the phase first is what stops this and the
    // branch below from driving each other in a loop — the location change
    // each one causes finds nothing left to do.
    if (open && phase.current === "owned" && !onOverlayEntry) {
      phase.current = "idle";
      dismiss.current();
      return;
    }

    // Closed from inside the app instead (a button, Escape, dragging the
    // handle down) — take our entry back off the stack, or a later back press
    // would return to it and land on a screen with no overlay on it.
    if (!open && phase.current === "owned") {
      phase.current = "idle";
      navigate(-1);
    }
  }, [key, location.key, location.pathname, location.search, location.state, navigate, onOverlayEntry, open]);
}
